import getStreamCDNPublicUrl from "@/libs/get-cdn-public-url";
import { makeAutoObservable, runInAction } from "mobx";
// @ts-ignore
import firebase from "@/libs/firebase";
import { getIdToken } from "firebase/auth";
import shaka from "shaka-player";

export interface PostVideoAsset {
  id?: string;
  type?: string;
  creatorId?: string;
  mp4?: {
    "720p"?: string;
    "1080p"?: string;
  };
  drmFreeStream?: {
    dash?: string;
    hls?: string;
  };
  blurStream?: {
    dash?: string;
    hls?: string;
  };
  thumbnail?: {
    clear?: string;
    blur?: string;
  };
}

export class PostPlayerStore {
  player: shaka.Player;
  videoRef: HTMLVideoElement | null = null;
  currentAsset: PostVideoAsset | null = null;
  isPlaying = false;
  isBuffering = false;
  private attachedRef: HTMLVideoElement | null = null;
  private cachedAuthToken: string | null = null;
  private tokenExpiryTime: number = 0;

  get isVideoAttached(): boolean {
    return this.videoRef !== null && this.videoRef === this.attachedRef;
  }

  constructor() {
    shaka.polyfill.installAll();
    this.player = new shaka.Player();
    this.player.configure({
      streaming: {
        // Increase buffer to prevent interruptions with 4s GOP segments
        // Target buffer: 16 seconds (4x segment duration) to ensure smooth playback
        bufferingGoal: 16,
        // Minimum buffer before resuming after rebuffering
        rebufferingGoal: 8,
        // Keep some buffer behind the playhead
        bufferBehind: 30,
        retryParameters: {
          timeout: 30000,
          maxAttempts: 3,
          baseDelay: 1000,
          backoffFactor: 2,
          fuzzFactor: 0.5,
        },
      },
      manifest: {
        retryParameters: {
          timeout: 30000,
          maxAttempts: 3,
          baseDelay: 1000,
          backoffFactor: 2,
          fuzzFactor: 0.5,
        },
      },
      abr: {
        // Prefer lower bandwidth tracks by making upgrades harder and downgrades easier
        bandwidthUpgradeFactor: 2.0, // Higher = more conservative (harder to upgrade quality)
        bandwidthDowngradeFactor: 0.85, // Lower = more aggressive (easier to downgrade)
        // Use a lower initial bandwidth estimate to start with lower quality
        initialBandwidthEstimate: 1000000, // 1 Mbps initial estimate (conservative)
        // Restrict to video element size to avoid unnecessary high resolutions
        restrictToElementSize: true,
        // Minimum time between quality switches (in seconds)
        switchInterval: 8,
        // Use network information API if available
        useNetworkInformation: true,
        restrictions: {
          // Optionally restrict max resolution (uncomment if needed)
          // maxWidth: 1280,
          // maxHeight: 720,
          // maxFrameRate: 30,
          // maxBandwidth: 2000000, // 2 Mbps max bandwidth
        },
      },
    });
    
    // Pre-fetch and cache auth token
    this.refreshAuthToken();
    
    this.player.addEventListener("error", (event: any) => {
      // Handle Shaka Player error
    });
    
    makeAutoObservable(this);
  }

  private async refreshAuthToken() {
    const currentUser = firebase.auth?.currentUser;
    if (currentUser) {
      try {
        const token = await getIdToken(currentUser);
        if (token) {
          this.cachedAuthToken = token;
          // Cache for 50 minutes (tokens typically last 1 hour)
          this.tokenExpiryTime = Date.now() + 50 * 60 * 1000;
        }
      } catch (error) {
        this.cachedAuthToken = null;
      }
    } else {
      this.cachedAuthToken = null;
    }
  }


  setVideoRef(ref: HTMLVideoElement | null) {
    if (!ref && this.videoRef === this.attachedRef) {
      this.player.detach();
      this.attachedRef = null;
      this.videoRef = null;
      return;
    }

    if (ref && ref !== this.attachedRef) {
      if (this.attachedRef) {
        this.player.detach();
      }
      
      if (ref.crossOrigin !== "anonymous") {
        ref.crossOrigin = "anonymous";
      }
      
      this.videoRef = ref;
      this.attachedRef = ref;
      
      this.player.attach(ref);
      
      ref.addEventListener("loadstart", this.handleLoadStart);
      ref.addEventListener("play", this.handlePlay);
      ref.addEventListener("pause", this.handlePause);
      ref.addEventListener("error", (e) => {
        // Handle video element error
      });
    }
  }

  private handleLoadStart = () => {
    runInAction(() => {
      this.isBuffering = true;
    });
  };

  private handlePlay = () => {
    runInAction(() => {
      this.isBuffering = false;
      this.isPlaying = true;
    });
  };

  private handlePause = () => {
    runInAction(() => {
      this.isPlaying = false;
    });
  };

  /**
   * Get the stream URL for a post video asset.
   * Always uses HLS. Uses proxy URL for drmFreeStream.hls with token in query parameter.
   * Falls back to blurStream.hls if subscription check fails (403).
   */
  async getStreamUrl(asset: PostVideoAsset | null): Promise<string | null> {
    if (!asset || (!asset.drmFreeStream && !asset.blurStream)) {
      return null;
    }

    const drmFreeHls = asset.drmFreeStream?.hls;
    const blurHls = asset.blurStream?.hls;
    const mp4720p = asset.mp4?.["720p"];

    // If no drmFreeStream.hls, use blurStream.hls
    if (!drmFreeHls) {
      if (blurHls) {
        const blurUrl = getStreamCDNPublicUrl(blurHls);
        return blurUrl;
      }
      return null;
    }

    // Try to extract creatorId from the path if not provided
    // Path format: /posts/{postId}/media/{mediaId}/hls/...
    // We'll try the proxy URL anyway - backend might handle missing creatorId
    let finalCreatorId = asset.creatorId;
    if (!finalCreatorId && drmFreeHls) {
      const pathMatch = drmFreeHls.match(/\/posts\/([^/]+)\//);
    }

    // Try to use drmFreeStream.hls with proxy URL
    const mediaApiBaseUrl = import.meta.env.VITE_MEDIA_PROXY_SERVER;
    if (!mediaApiBaseUrl) {
      if (blurHls) {
        const blurUrl = getStreamCDNPublicUrl(blurHls);
        return blurUrl;
      }
      return null;
    }

    // Get token for query parameter
    let token = this.cachedAuthToken;
    if (!token) {
      const currentUser = firebase.auth?.currentUser;
      if (currentUser) {
        try {
          token = await getIdToken(currentUser);
          if (token) {
            this.cachedAuthToken = token;
            this.tokenExpiryTime = Date.now() + 50 * 60 * 1000;
          }
        } catch (error) {
          // Failed to get token
        }
      }
    }

    // Build proxy URL
    const mediaPath = drmFreeHls ? drmFreeHls.startsWith('/') ? drmFreeHls : `/${drmFreeHls}` : mp4720p ? mp4720p.startsWith('/') ? mp4720p : `/${mp4720p}` : "";
    
    // Build base proxy URL
    let proxyUrl = finalCreatorId 
      ? `${mediaApiBaseUrl}/media/proxy?path=${encodeURIComponent(mediaPath)}&creatorId=${finalCreatorId}`
      : `${mediaApiBaseUrl}/media/proxy?path=${encodeURIComponent(mediaPath)}`;
    
    // Append token as query parameter if available
    if (token) {
      proxyUrl += `&token=${encodeURIComponent(token)}`;
    }

    // Check subscription status using HEAD request
    try {
      const response = await fetch(proxyUrl, {
        method: 'HEAD',
      });

      // If 403, user is not subscribed, use blurStream
      if (response.status === 403) {
        if (blurHls) {
          const blurUrl = getStreamCDNPublicUrl(blurHls);
          return blurUrl;
        }
        return null;
      }

      // If successful (200, 206, etc.), use proxy URL
      if (response.ok) {
        return proxyUrl;
      }

      // For other errors, fallback to blurStream
      if (blurHls) {
        const blurUrl = getStreamCDNPublicUrl(blurHls);
        return blurUrl;
      }
      return null;
    } catch (error) {
      // On error, fallback to blurStream
      if (blurHls) {
        const blurUrl = getStreamCDNPublicUrl(blurHls);
        return blurUrl;
      }
      return null;
    }
  }

  async load(asset: PostVideoAsset | null) {
    if (!asset || !this.videoRef || this.videoRef !== this.attachedRef) {
      return;
    }

    // CRITICAL: Refresh token BEFORE getting stream URL and loading
    // This ensures token is cached when Shaka Player makes requests
    await this.refreshAuthToken();
        
    const streamUrl = await this.getStreamUrl(asset);
    if (!streamUrl) {
      return;
    }

    if (this.currentAsset?.id === asset.id) {
      return;
    }

    runInAction(() => {
      this.currentAsset = asset;
      this.isBuffering = true;
    });

    try {
      try {
        await this.player.unload();
      } catch (e) {
        // Ignore unload errors
      }

      // Refresh token one more time right before loading to ensure it's absolutely fresh
      await this.refreshAuthToken();
      await new Promise(resolve => setTimeout(resolve, 50));
      await this.player.load(streamUrl);
      
      runInAction(() => {
        this.isBuffering = false;
      });
    } catch (error: any) {
      const shakaError = error;
      
      // If error is 403, try fallback to blurStream
      if (shakaError?.code === 4032 || (error instanceof Error && error.message?.includes('403'))) {
        const blurHls = asset.blurStream?.hls;
        if (blurHls) {
          const blurUrl = getStreamCDNPublicUrl(blurHls);
          try {
            await this.player.load(blurUrl);
            runInAction(() => {
              this.isBuffering = false;
            });
            return;
          } catch (fallbackError) {
            // Error loading blurStream fallback
          }
        }
      }
      
      runInAction(() => {
        this.isBuffering = false;
      });
    }
  }

  setMuted(muted: boolean) {
    if (this.videoRef) {
      this.videoRef.muted = muted;
    }
  }

  play() {
    return this.videoRef?.play();
  }

  pause() {
    return this.videoRef?.pause();
  }

  async unload() {
    try {
      await this.player.unload();
      runInAction(() => {
        this.currentAsset = null;
        this.isPlaying = false;
        this.isBuffering = false;
      });
    } catch (error) {
      // Error unloading player
    }
  }
}

export const postPlayerStore = new PostPlayerStore();

